import React from 'react';
import { useState } from 'react';
import { Home, Users, Clock, Star, Trash2, Cloud, Folder, Plus, ChevronRight, ChevronDown, X } from 'lucide-react';
import { FileItem } from '../types';
import { formatFileSize } from '../utils/fileUtils';
import { WorkspaceSwitcherPopup } from '@/components/Sidebar/WorkspaceSwitcherPopup';


// Helper function to check if a folder should be expanded
const isDescendantOrSelf = (descendantCandidateId: string, ancestorCandidateId: string, files: FileItem[]): boolean => {
  if (descendantCandidateId === ancestorCandidateId) {
    return true;
  }

  let currentId = descendantCandidateId;
  const visited = new Set<string>();

  while (currentId) {
    if (visited.has(currentId)) {
      // Prevent infinite loops in case of circular references
      break;
    }
    visited.add(currentId);

    const currentFolder = files.find(f => f.id === currentId);
    if (!currentFolder || !currentFolder.parentId) {
      break;
    }

    currentId = currentFolder.parentId;
    if (currentId === ancestorCandidateId) {
      return true;
    }
  }

  return false;
};

interface SidebarProps {
  files: FileItem[];
  currentFolderId?: string;
  onFolderSelect: (folderId?: string) => void;
  storageInfo: { used: number; total: number };
  currentFilter: string;
  isGlobalDarkMode?: boolean;
  onCreateFolder?: () => void;
  isLoading?: boolean;
  isMobile?: boolean;
  onClose?: () => void;
}

interface FolderTreeItemProps {
  folder: FileItem;
  files: FileItem[];
  currentFolderId?: string;
  onFolderSelect: (folderId?: string) => void;
  level: number;
  isGlobalDarkMode?: boolean;
}

const FolderTreeItem: React.FC<FolderTreeItemProps> = ({
  folder,
  files,
  currentFolderId,
  onFolderSelect,
  level,
  isGlobalDarkMode,
}) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    if (!currentFolderId) return false;
    return isDescendantOrSelf(currentFolderId, folder.id, files);
  });
  const subfolders = files.filter(file => file.type === 'folder' && file.parentId === folder.id && !file.trashed);
  const hasSubfolders = subfolders.length > 0;

  // Update expansion state when currentFolderId changes
  React.useEffect(() => {
    if (currentFolderId && isDescendantOrSelf(currentFolderId, folder.id, files)) {
      setIsExpanded(true);
    }
  }, [currentFolderId, files, folder.id]);

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasSubfolders) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <>
      <li>
        <div className="flex items-center">
          <button
            onClick={handleToggleExpand}
            className={`p-1 rounded transition-colors duration-200 ${hasSubfolders ? '' : 'invisible'} ${isGlobalDarkMode ? 'hover:bg-sidebar-accent text-sidebar-foreground/60' : 'hover:bg-sidebar-light-hover text-gray-500'}`}
            style={{ marginLeft: `${level * 12}px` }}
          >
            {hasSubfolders && (
              isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => onFolderSelect(folder.id)}
            className={`flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 text-left border ${
              currentFolderId === folder.id
                ? isGlobalDarkMode
                  ? 'bg-nav-active text-white shadow-md border-transparent'
                  : 'bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200'
                : isGlobalDarkMode
                  ? 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
            }`}
          >
            <Folder className={`w-4 h-4 flex-shrink-0 ${currentFolderId === folder.id ? (isGlobalDarkMode ? 'text-white' : 'text-gray-900') : (isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500')}`} />
            <span className="truncate">{folder.name}</span>
          </button>
        </div>
      </li>
      {isExpanded && hasSubfolders && (
        <>
          {subfolders.map((subfolder) => (
            <FolderTreeItem
              key={subfolder.id}
              folder={subfolder}
              files={files}
              currentFolderId={currentFolderId}
              onFolderSelect={onFolderSelect}
              level={level + 1}
              isGlobalDarkMode={isGlobalDarkMode}
            />
          ))}
        </>
      )}
    </>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  files,
  currentFolderId,
  onFolderSelect,
  storageInfo,
  currentFilter,
  isGlobalDarkMode,
  onCreateFolder,
  isLoading = false,
  isMobile = false,
  onClose
}) => {
  const folders = files.filter(file => file.type === 'folder' && !file.trashed);
  const rootFolders = folders.filter(folder => !folder.parentId);

  const usagePercentage = (storageInfo.used / storageInfo.total) * 100;

  const sidebarItems = [
    { icon: Home, label: 'My Drive', id: undefined },
    { icon: Users, label: 'Shared with me', id: 'shared' },
    { icon: Star, label: 'Bookmarked', id: 'bookmarked' },
    { icon: Clock, label: 'Recent', id: 'recent' },
    { icon: Trash2, label: 'Trash', id: 'trash' },
  ];

  const handleFolderClick = (folderId?: string) => {
    onFolderSelect(folderId);
    if (isMobile && onClose) {
      onClose();
    }
  };

  return (
    <aside
      className={`${isMobile ? 'fixed left-0 top-0 w-80 max-w-[85vw] h-full' : 'fixed left-16 top-14 w-56 h-[calc(100vh-3.5rem)]'} border-r flex flex-col z-20 ${
        isGlobalDarkMode
          ? 'bg-sidebar border-sidebar-border'
          : 'bg-sidebar-light-secondary border-gray-200'
      }`}
    >
      {isMobile && onClose && (
        <div className={`px-4 py-3 border-b flex items-center justify-end ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isGlobalDarkMode ? 'hover:bg-sidebar-accent text-sidebar-foreground/80' : 'hover:bg-sidebar-light-hover text-gray-600'}`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      <nav className="flex-1 p-3">
        <ul className="space-y-1">
          {sidebarItems.map((item) => {
            const isActive = (item.id === undefined && currentFilter === 'all' && !currentFolderId) ||
              (item.id !== undefined && currentFilter === item.id);

            return (
              <li key={item.id || 'home'}>
                <button
                  onClick={() => handleFolderClick(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border ${
                    isActive
                      ? isGlobalDarkMode
                        ? 'bg-nav-active text-white shadow-md border-transparent'
                        : 'bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200'
                      : isGlobalDarkMode
                        ? 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent border-transparent'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent'
                  }`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="mt-6">
          <div className={`border-t mb-2 ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`} />

          <div className="flex items-center justify-between px-3 mb-2">
            <h3 className={`text-xs font-semibold uppercase tracking-wider ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>
              Folders
            </h3>
            {onCreateFolder && (
              <button
                onClick={onCreateFolder}
                className={`p-1 rounded transition-colors duration-200 ${isGlobalDarkMode ? 'hover:bg-sidebar-accent text-sidebar-foreground/60 hover:text-sidebar-foreground' : 'hover:bg-sidebar-light-hover text-gray-500 hover:text-gray-700'}`}
                title="Create new folder"
              >
                <Plus size={14} />
              </button>
            )}
          </div>

          <div className={`border-t mb-2 ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`} />

          {isLoading ? (
            <ul className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <li key={i} className="px-3">
                  <div className={`h-8 w-full rounded-lg animate-pulse ${isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-gray-100'}`} />
                </li>
              ))}
            </ul>
          ) : rootFolders.length > 0 && (
            <ul className="space-y-1">
              {rootFolders.map((folder) => (
                <FolderTreeItem
                  key={folder.id}
                  folder={folder}
                  files={files}
                  currentFolderId={currentFolderId}
                  onFolderSelect={handleFolderClick}
                  level={0}
                  isGlobalDarkMode={isGlobalDarkMode}
                />
              ))}
            </ul>
          )}
        </div>
      </nav>

      <div className={`p-4 border-t ${isMobile ? 'pb-20' : ''} ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2 mb-2">
          <Cloud size={16} className={isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'} />
          <span className={`text-sm ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>Storage</span>
        </div>
        <div className={`w-full rounded-full h-2 mb-2 ${isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-gray-200'}`}>
          <div
            className="bg-nav-active h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(usagePercentage, 100)}%` }}
          />
        </div>
        <p className={`text-xs ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>
          {formatFileSize(storageInfo.used)} of {formatFileSize(storageInfo.total)} used
        </p>
      </div>
    </aside>
  );
};

